import { Suspense } from 'react'
import { Outlet, useNavigate } from 'react-router'
import {
  FormattedMessage,
  FormattedRelativeTime,
  FormattedDate,
  useIntl
} from 'react-intl'
import {
  getRelativeTimeProps,
  shouldUseRelativeTime
} from 'src/utils/dateUtils'
import { useTokens, useTokenCRUD } from '@hooks/useTokens'
import ErrorBoundary from '@components/ErrorBoundary/ErrorBoundary'
import ErrorMessage from '@components/ErrorBoundary/ErrorMessage'
import ItemListSkeleton from '@components/skeletons/ItemListSkeleton/ItemListSkeleton'
import ProfileEmptyList from '../ProfileEmptyList/ProfileEmptyList'

import './TokensList.css'

const TokensList = () => {
  const items = useTokens()
  const navigate = useNavigate()
  const intl = useIntl()
  const { revokeToken } = useTokenCRUD()

  const handleDelete = async (id: string) => {
    const isConfirmed = window.confirm(
      intl.formatMessage({ id: 'profile.tokens.delete-confirm' })
    )

    if (!isConfirmed) return

    try {
      await revokeToken(id)
    } catch (error) {
      console.error('Error al revocar el token:', error)
    }
  }

  if (!items || items.length === 0) {
    return <ProfileEmptyList type="tokens" />
  }

  return (
    <ul id="token-item-list">
      {items.map((item) => {
        const createdIsRecent = shouldUseRelativeTime(item.created)
        const createdProps = getRelativeTimeProps(item.created)

        return (
          <li key={item?.id} className="token-item">
            <section className="token-item-info">
              <span className="icon icon-key" />
              <h3
                title={item?.name || ''}
                className="token-item-title"
                onClick={() => navigate(`info/${item?.id}`)}
              >
                <span className="ellipsis">{item?.name || '---'}</span>
              </h3>
              <div className="token-item-meta">
                <span className="meta-text expires">
                  <FormattedMessage
                    id="expires"
                    values={{
                      time: (
                        <FormattedDate
                          key="token-expires-date"
                          value={item.expires}
                          year="numeric"
                          month="short"
                          day="numeric"
                        />
                      )
                    }}
                  />
                </span>
                <span className="separator">•</span>
                <em className="meta-text created">
                  (
                  <FormattedMessage
                    id="created"
                    values={{
                      time: createdIsRecent ? (
                        <FormattedRelativeTime
                          key="token-created-relative"
                          value={createdProps.value}
                          unit={createdProps.unit}
                        />
                      ) : (
                        <FormattedDate
                          key="token-created-date"
                          value={item.created}
                          year="numeric"
                          month="short"
                          day="numeric"
                        />
                      )
                    }}
                  />
                  )
                </em>
              </div>
            </section>
            <section className="token-item-actions">
              <span
                role="button"
                className="icon icon-bin"
                onClick={() => handleDelete(item.id)}
              />
            </section>
          </li>
        )
      })}
    </ul>
  )
}

export default function TokensListWrapper() {
  return (
    <>
      <ErrorBoundary errorComponent={ErrorMessage}>
        <Suspense fallback={<ItemListSkeleton isToken />}>
          <TokensList />
        </Suspense>
      </ErrorBoundary>
      <Outlet />
      <a
        className="profile-doc"
        target="_blank"
        href="https://catalog.datalab.adigital.org/docs/."
        rel="noreferrer noopener"
      >
        <FormattedMessage id="profile.doc" />
      </a>
    </>
  )
}
